// app/src/You.jsx — profile (name + language), workplace, sign out.
//
// Profile edits go to PATCH /me/preferences, which updates the display name +
// preferred language on every membership (so the person looks consistent across
// workplaces). Language also drives the UI chrome (window.setLang) and content
// translation (the member's preferredLang).

// Monday 00:00 of the current week; a shift counts toward "this week" if it starts in it.
function weekStartMs() { const x = new Date(); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x.getTime(); }

function YouScreen() {
  const nav = window.useNav();
  const [uiLang, setUiLang] = window.useLang();
  const wid = window.crew && window.crew.state.activeWorkplaceId;
  const me = (window.crew && window.crew.state.me) || { workplaces: [], profile: {} };
  const wp = (me.workplaces || []).find((w) => w.workplaceId === wid) || {};

  const [name, setName] = React.useState(wp.displayName || '');
  const [lang, setLang] = React.useState(wp.preferredLang || uiLang || 'en');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [avatarUrl, setAvatarUrl] = React.useState((me.profile && me.profile.avatarUrl) || null);
  const [uploadingAvatar, setUploadingAvatar] = React.useState(false);
  const [cropFile, setCropFile] = React.useState(null);
  const avatarRef = React.useRef(null);

  // Lightweight stats for the profile tiles (crew size + my shifts/hours this week).
  const [crewCount, setCrewCount] = React.useState(null);
  const [weekStats, setWeekStats] = React.useState(null); // { hours, shifts }
  const myId = me.profile && me.profile.userId;
  React.useEffect(() => {
    if (!wid || !myId) return;      // wait for the profile to hydrate (myId in deps)
    let live = true;               // ignore responses after unmount / workplace switch
    window.crew.api(`/workplaces/${wid}/members`, { workplaceId: wid })
      .then((r) => { if (live) setCrewCount(((r && r.members) || []).length); }).catch(() => {});
    window.crew.api(`/workplaces/${wid}/shifts`, { workplaceId: wid }).then((r) => {
      if (!live) return;
      const ws = weekStartMs();
      const mine = ((r && r.shifts) || []).filter((s) => s.assigneeId === myId && s.start >= ws && s.start < ws + 7 * 864e5);
      const hours = mine.reduce((a, s) => a + (s.start && s.end ? (s.end - s.start) / 36e5 : 0), 0);
      setWeekStats({ hours: Math.round(hours * 10) / 10, shifts: mine.length });
    }).catch(() => {});
    return () => { live = false; };
  }, [wid, myId]);

  // Pick → open the round-crop modal (no upload until the user frames the photo).
  const pickAvatar = (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (file && file.type.startsWith('image/')) setCropFile(file);
  };

  // Upload the cropped square: presign → PUT to S3 → save the key on the profile
  // (PATCH /me) so it shows everywhere the person appears.
  const uploadAvatar = async (blob) => {
    setCropFile(null);
    if (!blob || uploadingAvatar) return;
    setUploadingAvatar(true);
    try {
      const pres = await window.crew.api('/me/avatar/presign', { method: 'POST', body: { contentType: blob.type || 'image/jpeg' } });
      await fetch(pres.putUrl, { method: 'PUT', headers: { 'content-type': pres.contentType }, body: blob });
      const res = await window.crew.api('/me/preferences', { method: 'PATCH', body: { avatarKey: pres.key } });
      const newUrl = (res && res.avatarUrl) || URL.createObjectURL(blob);
      setAvatarUrl(newUrl);
      if (me.profile) me.profile.avatarUrl = newUrl; // reflect across screens this session
    } catch (err) { console.warn('[profile] avatar upload failed', err && err.message); }
    finally { setUploadingAvatar(false); }
  };

  const dirty = name.trim() && (name.trim() !== (wp.displayName || '') || lang !== (wp.preferredLang || ''));

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true); setSaved(false);
    try {
      await window.crew.api('/me/preferences', { method: 'PATCH', body: { displayName: name.trim(), preferredLang: lang } });
      // Reflect locally so other screens update without a reload.
      (me.workplaces || []).forEach((w) => { w.displayName = name.trim(); w.preferredLang = lang; });
      window.setLang(lang); // keep UI chrome language in sync
      setSaved(true);
    } catch (e) {
      console.warn('[profile] save failed', e && e.message);
    } finally { setSaving(false); }
  };

  const card = { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 12 };
  const field = { width: '100%', padding: '11px 13px', marginTop: 6, marginBottom: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 15, boxSizing: 'border-box' };
  const label = { fontSize: 12, fontWeight: 700, color: 'var(--sub)' };

  return (
    <KShell tab="you">
      <KHeader title={t('nav.you', 'You')} />
      <div style={{ padding: '12px 16px' }}>

        {/* Identity header — sits on the purple canvas (light text). */}
        <input ref={avatarRef} type="file" accept="image/*" onChange={pickAvatar} style={{ display: 'none' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 2px 18px' }}>
          <Avatar name={name || (me.profile && me.profile.name)} url={avatarUrl} size={64} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 22, color: 'var(--on)', lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || (me.profile && me.profile.name) || t('common.member', 'Member')}</div>
            <div style={{ fontSize: 12.5, color: 'var(--on-soft)', marginTop: 2 }}>{[wp.role ? t('role.' + wp.role, wp.role) : '', wp.name].filter(Boolean).join(' · ') || t('role.member', 'Member')}</div>
            <button onClick={() => avatarRef.current && avatarRef.current.click()} disabled={uploadingAvatar} style={{
              marginTop: 8, padding: '5px 11px', borderRadius: 999, border: '1px solid var(--line-on)',
              background: 'rgba(255,255,255,.06)', color: 'var(--on)', fontWeight: 700, fontSize: 11.5, cursor: uploadingAvatar ? 'default' : 'pointer', opacity: uploadingAvatar ? 0.6 : 1,
            }}>{uploadingAvatar ? t('common.saving', 'Saving…') : t('you.changePhoto', 'Change photo')}</button>
          </div>
        </div>

        {/* Stat tiles */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          {[
            { n: weekStats ? `${weekStats.hours}h` : '—', l: t('you.statWeek', 'This week') },
            { n: weekStats ? String(weekStats.shifts) : '—', l: t('you.statShifts', 'Shifts') },
            { n: crewCount != null ? String(crewCount) : '—', l: t('you.statCrew', 'Crew') },
          ].map((s, i) => (
            <div key={i} style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', padding: '14px 8px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 22, color: 'var(--ink)' }}>{s.n}</div>
              <div style={{ fontSize: 11, color: 'var(--sub)', marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Profile (name + language) */}
        <section style={card}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sub)', marginBottom: 10 }}>{t('you.profile', 'YOUR PROFILE')}</div>
          <div style={label}>{t('you.name', 'Name')}</div>
          <input style={field} value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} placeholder={t('you.namePlaceholder', 'Your name')} />
          <div style={label}>{t('common.language', 'Language')}</div>
          <select style={field} value={lang} onChange={(e) => { setLang(e.target.value); setSaved(false); }}>
            {window.CREW_LANGS.map((l) => <option key={l} value={l}>{window.langName(l)}</option>)}
          </select>
          <button onClick={save} disabled={!dirty || saving} style={{
            width: '100%', padding: '11px 16px', cursor: dirty && !saving ? 'pointer' : 'default', borderRadius: 'var(--radius-sm)',
            border: 'none', background: 'var(--primary)', color: 'var(--primary-ink)', fontWeight: 700, fontSize: 14,
            opacity: dirty && !saving ? 1 : 0.6,
          }}>{saving ? t('common.saving', 'Saving…') : saved ? t('common.saved', '✓ Saved') : t('common.save', 'Save')}</button>
        </section>

        {/* Workplace */}
        <section style={card}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sub)', marginBottom: 4 }}>{t('you.workplace', 'WORKPLACE')}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>{wp.name || '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--sub)', marginBottom: 12 }}>{t('you.roleLine', "You're {role}", { role: wp.role ? t('role.' + wp.role, wp.role) : t('role.member', 'a member') })}</div>
          <button onClick={() => nav.navigate('/people')} style={{
            width: '100%', padding: '11px 16px', cursor: 'pointer', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontWeight: 700, fontSize: 14,
          }}>{t('you.peopleInvites', 'People & invites')}</button>
          {wp.role === 'owner' && (
            <button onClick={() => nav.navigate('/billing')} style={{
              width: '100%', marginTop: 8, padding: '11px 16px', cursor: 'pointer', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontWeight: 700, fontSize: 14,
            }}>{t('you.subscriptionBilling', 'Subscription & billing')}</button>
          )}
        </section>

        <NotificationsToggle />

        <button onClick={() => window.crew.auth.signOut()} style={{
          width: '100%', padding: '12px 16px', cursor: 'pointer', borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontWeight: 700, fontSize: 14,
        }}>{t('common.signOut', 'Sign out')}</button>
      </div>
      {cropFile && <AvatarCropper file={cropFile} onCancel={() => setCropFile(null)} onCropped={uploadAvatar} />}
    </KShell>
  );
}

// "Enable notifications" — requests permission, subscribes this device for Web
// Push, and registers it (POST /me/devices). Hidden when the build/browser can't
// do push (no VAPID public key configured, or no ServiceWorker/PushManager).
function NotificationsToggle() {
  const supported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';
  const cfg = (window.crew && window.crew.getConfig && window.crew.getConfig()) || {};
  const [perm, setPerm] = React.useState(supported ? Notification.permission : 'unsupported');
  const [busy, setBusy] = React.useState(false);
  if (!supported || !cfg.vapidPublicKey) return null;

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const p = await Notification.requestPermission();
      setPerm(p);
      if (p !== 'granted') return;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(cfg.vapidPublicKey) });
      await window.crew.api('/me/devices', { method: 'POST', body: { subscription: sub.toJSON(), platform: 'web' } });
    } catch (e) { console.warn('[push] enable failed', e && e.message); }
    finally { setBusy(false); }
  };

  const card = { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 12 };
  return (
    <section style={card}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sub)', marginBottom: 10 }}>{t('you.notifications', 'NOTIFICATIONS')}</div>
      {perm === 'granted' ? (
        <div style={{ fontSize: 13, color: 'var(--ok)', fontWeight: 600 }}>{t('you.notificationsOn', '✓ Notifications are on')}</div>
      ) : perm === 'denied' ? (
        <div style={{ fontSize: 12.5, color: 'var(--sub)' }}>{t('you.notificationsBlocked', 'Notifications are blocked in your browser settings.')}</div>
      ) : (
        <button onClick={enable} disabled={busy} style={{
          width: '100%', padding: '11px 16px', cursor: busy ? 'default' : 'pointer', borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontWeight: 700, fontSize: 14, opacity: busy ? 0.6 : 1,
        }}>{busy ? t('you.enabling', 'Enabling…') : t('you.enableNotifications', 'Enable notifications')}</button>
      )}
    </section>
  );
}

// base64url VAPID key → Uint8Array for pushManager.subscribe.
function urlB64ToUint8(b64) {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

Object.assign(window, { YouScreen });
